// Main branch data for realistic local SEO
export const mainBranch = {
  id: "pathanamthitta-main",
  name: "XPLOD-GROUPS",
  city: "Pathanamthitta",
  address: "SH8, Kumbazha, Mylapra, Pathanamthitta, Kerala 689643",
  phone: "+91-9446189273",
  coordinates: { lat: 9.263078942697597, lng: 76.80643844069269 },
  hours: {
    open: "09:00",
    close: "18:00",
    days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  },
  services: ["Sales", "Service", "Spare Parts", "Training", "Delivery"],
};

// Service areas covered from main branch (realistic approach)
export const keralaServiceAreas = [
  {
    district: "Pathanamthitta",
    cities: ["Pathanamthitta", "Thiruvalla", "Mallappally", "Kozhencherry"],
    distance: "0-25km",
    priority: 1,
  },
  {
    district: "Kottayam",
    cities: ["Kottayam", "Changanassery", "Pala", "Ettumanoor"],
    distance: "25-50km",
    priority: 2,
  },
  {
    district: "Alappuzha",
    cities: ["Alappuzha", "Chengannur", "Mavelikkara", "Kayamkulam"],
    distance: "30-60km",
    priority: 2,
  },
  {
    district: "Kollam",
    cities: ["Kollam", "Karunagappally", "Punalur", "Anchal"],
    distance: "40-70km",
    priority: 3,
  },
  {
    district: "Thiruvananthapuram",
    cities: ["Thiruvananthapuram", "Attingal", "Nedumangad", "Varkala"],
    distance: "70-100km",
    priority: 3,
  },
  {
    district: "Idukki",
    cities: ["Thodupuzha", "Kumily", "Munnar", "Kattappana"],
    distance: "50-80km",
    priority: 3,
  },
];

export const generateProductSchema = (product, branchId = null) => {
  if (!product) return null;

  const images = [];
  if (product.images && Array.isArray(product.images)) {
    product.images.forEach((img) => {
      if (typeof img === "string") {
        images.push(img);
      } else if (img.image_url) {
        images.push(img.image_url);
      }
    });
  }

  const branch = mainBranch;

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description:
      product.description ||
      product.brief_description ||
      product.short_description ||
      `${product.name} - Professional quality tool from XPLOD-GROUPS`,
    image:
      images.length > 0
        ? images
        : [
            "https://res.cloudinary.com/dmyb3ffz9/image/upload/v1756269583/image_1_wfi4lf.webp",
          ],
    brand: {
      "@type": "Brand",
      name: product.brand || "STIHL",
    },
    sku: product.sku || `SKU-${product.id}`,
    mpn: product.sku || product.id,
    offers: {
      "@type": "Offer",
      url: `https://xplod-groups.com/products/${product.id}`,
      priceCurrency: "INR",
      price: product.price || "0",
      availability:
        product.inventory_quantity > 0 || product.status === "active"
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      availableAtOrFrom: branch
        ? {
            "@type": "Place",
            name: branch.name,
            address: {
              "@type": "PostalAddress",
              streetAddress: branch.address,
              addressRegion: "Kerala",
              addressCountry: "IN",
            },
          }
        : undefined,
      seller: {
        "@type": "Organization",
        name: "XPLOD-GROUPS",
        url: "https://xplod-groups.com",
      },
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.5",
      reviewCount: "23",
    },
  };
};

export const generateBreadcrumbSchema = (items) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `https://xplod-groups.com${item.url}`,
    })),
  };
};

export const generateCategorySchema = (category, products) => {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: category.name || category,
    description:
      category.description ||
      `Browse our collection of ${category.name || category}`,
    url: `https://xplod-groups.com/collections/${category.slug || category}`,
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: products.length,
      itemListElement: products.slice(0, 10).map((product, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `https://xplod-groups.com/products/${product.id}`,
        name: product.name,
      })),
    },
  };
};

export const generateLocalBusinessSchema = () => {
  const branch = mainBranch;

  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://xplod-groups.com",
    name: branch.name,
    alternateName: "XPLOD GROUPS",
    image:
      "https://res.cloudinary.com/dmyb3ffz9/image/upload/v1756269583/image_1_wfi4lf.webp",
    url: "https://xplod-groups.com",
    telephone: branch.phone,
    priceRange: "₹₹₹",
    address: {
      "@type": "PostalAddress",
      streetAddress: branch.address,
      addressLocality: branch.city,
      addressRegion: "Kerala",
      addressCountry: "IN",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: branch.coordinates.lat,
      longitude: branch.coordinates.lng,
    },
    areaServed: keralaServiceAreas.map((area) => ({
      "@type": "AdministrativeArea",
      name: area.district,
      containedInPlace: {
        "@type": "State",
        name: "Kerala",
        containedInPlace: {
          "@type": "Country",
          name: "India",
        },
      },
    })),
    serviceArea: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: branch.coordinates.lat,
        longitude: branch.coordinates.lng,
      },
      geoRadius: "100000", // 100km radius covering most of Kerala
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: branch.hours.days,
        opens: branch.hours.open,
        closes: branch.hours.close,
      },
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Power Tools & Garden Equipment",
      itemListElement: [
        {
          "@type": "OfferCatalog",
          name: "Chainsaws",
        },
        {
          "@type": "OfferCatalog",
          name: "Blowers",
        },
        {
          "@type": "OfferCatalog",
          name: "Brushcutters",
        },
        {
          "@type": "OfferCatalog",
          name: "Lawn Mowers",
        },
        {
          "@type": "OfferCatalog",
          name: "Pressure Washers",
        },
      ],
    },
    makesOffer: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Home Delivery",
          description: "Free delivery within 50km, paid delivery across Kerala",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Installation Service",
          description: "Professional installation and setup",
        },
      },
    ],
    sameAs: [
      "https://www.facebook.com/xplodgroups",
      "https://www.instagram.com/xplodgroups",
    ],
  };
};

export const generateServiceAreaSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Power Tools Sales & Service",
    description:
      "Professional power tools sales, service, and delivery across Kerala",
    provider: {
      "@type": "LocalBusiness",
      name: "XPLOD-GROUPS",
      url: "https://xplod-groups.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: mainBranch.address,
        addressLocality: mainBranch.city,
        addressRegion: "Kerala",
        addressCountry: "IN",
      },
    },
    areaServed: keralaServiceAreas.flatMap((area) =>
      area.cities.map((city) => ({
        "@type": "City",
        name: city,
        containedInPlace: {
          "@type": "AdministrativeArea",
          name: area.district,
          containedInPlace: {
            "@type": "State",
            name: "Kerala",
          },
        },
      })),
    ),
    serviceType: [
      "Power Tools Sales",
      "Equipment Service & Repair",
      "Spare Parts Supply",
      "Home Delivery",
      "Installation Service",
      "Product Training",
      "Warranty Support",
    ],
    offers: [
      {
        "@type": "Offer",
        name: "Free Delivery",
        description: "Free delivery within 50km radius",
        eligibleRegion: {
          "@type": "GeoCircle",
          geoMidpoint: {
            "@type": "GeoCoordinates",
            latitude: mainBranch.coordinates.lat,
            longitude: mainBranch.coordinates.lng,
          },
          geoRadius: "50000",
        },
      },
      {
        "@type": "Offer",
        name: "Paid Delivery",
        description: "Delivery available across Kerala",
        eligibleRegion: {
          "@type": "State",
          name: "Kerala",
        },
      },
    ],
  };
};

export const generateOrganizationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "XPLOD-GROUPS",
    alternateName: "XPLOD GROUPS",
    url: "https://xplod-groups.com",
    logo: "https://res.cloudinary.com/dmyb3ffz9/image/upload/v1756269583/image_1_wfi4lf.webp",
    description:
      "Leading supplier of professional power tools and garden equipment in Kerala. Authorized dealer of STIHL and other premium brands.",
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: "+91-9446189273",
        contactType: "customer service",
        email: "info@xplod-groups.com",
        areaServed: "IN",
        availableLanguage: ["en", "Malayalam"],
      },
      {
        "@type": "ContactPoint",
        telephone: "+91-98765-43211",
        contactType: "sales",
        areaServed: "IN",
      },
    ],
    sameAs: [
      "https://www.facebook.com/xplodgroups",
      "https://www.instagram.com/xplodgroups",
    ],
    address: {
      "@type": "PostalAddress",
      streetAddress: "SH8, Kumbazha, Mylapra",
      addressLocality: "Pathanamthitta",
      addressRegion: "Kerala",
      postalCode: "689643",
      addressCountry: "IN",
    },
  };
};

export const generateSiteNavigationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "SiteNavigationElement",
    name: [
      "Home",
      "Chainsaws",
      "Blowers",
      "Brushcutters",
      "Lawn Mowers",
      "Pressure Washers",
      "About",
      "Contact",
      "Branches",
    ],
    url: [
      "https://xplod-groups.com",
      "https://xplod-groups.com/collections/chainsaws",
      "https://xplod-groups.com/collections/blowers",
      "https://xplod-groups.com/collections/brushcutters",
      "https://xplod-groups.com/collections/lawn-mowers",
      "https://xplod-groups.com/collections/pressure-washers",
      "https://xplod-groups.com/about",
      "https://xplod-groups.com/contact",
      "https://xplod-groups.com/branches",
    ],
  };
};

export const sanitizeForSEO = (text) => {
  if (!text) return "";
  if (typeof text === "object") {
    return String(
      text.text || text.content || text.description || JSON.stringify(text),
    );
  }
  return String(text)
    .replace(/<[^>]*>/g, "")
    .substring(0, 160);
};

export const generateMetaDescription = (product) => {
  if (!product) return "";

  const desc =
    product.brief_description ||
    product.description ||
    product.short_description ||
    "";
  const sanitized = sanitizeForSEO(desc);

  if (sanitized.length > 10) return sanitized;

  return `${product.name} - ${product.brand || "Professional quality"} tool available at XPLOD-GROUPS. Contact us for pricing and availability in Kerala.`;
};

export const generateMetaKeywords = (product, category, city = null) => {
  const keywords = [];

  if (product) {
    keywords.push(product.name);
    if (product.brand) keywords.push(product.brand);
    if (product.type) keywords.push(product.type);
    if (product.category_name) keywords.push(product.category_name);
  }

  if (category) {
    keywords.push(category);
  }

  // Add city-specific keywords
  if (city) {
    keywords.push(`${city}`, `power tools ${city}`, `STIHL ${city}`);
  }

  keywords.push(
    "Kerala",
    "power tools",
    "STIHL",
    "professional tools",
    "buy online",
    "authorized dealer",
  );

  return keywords.join(", ");
};

export const generateFAQSchema = (faqs) => {
  if (!faqs || !Array.isArray(faqs)) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
};

export const generateReviewSchema = (reviews) => {
  if (!reviews || !Array.isArray(reviews)) return null;

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "XPLOD-GROUPS",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue:
        reviews.reduce((acc, review) => acc + review.rating, 0) /
        reviews.length,
      reviewCount: reviews.length,
      bestRating: 5,
      worstRating: 1,
    },
    review: reviews.map((review) => ({
      "@type": "Review",
      author: {
        "@type": "Person",
        name: review.author,
      },
      reviewRating: {
        "@type": "Rating",
        ratingValue: review.rating,
        bestRating: 5,
      },
      reviewBody: review.text,
      datePublished: review.date,
    })),
  };
};

export const generateCityPageKeywords = (city) => {
  const baseKeywords = [
    `power tools ${city}`,
    `STIHL ${city}`,
    `chainsaws ${city}`,
    `garden tools ${city}`,
    `XPLOD-GROUPS ${city}`,
    `professional tools ${city}`,
    `authorized dealer ${city}`,
    `delivery ${city}`,
    `service ${city}`,
  ];

  return (
    baseKeywords.join(", ") + ", Kerala, India, home delivery, installation"
  );
};

export const generateCitySpecificSchema = (city, products = []) => {
  // Find service area info for this city
  const serviceArea = keralaServiceAreas.find((area) =>
    area.cities.includes(city),
  );

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `Power Tools ${city} - XPLOD-GROUPS`,
    description: `Professional power tools and garden equipment available in ${city}, Kerala. Authorized STIHL dealer with home delivery and installation service.`,
    url: `https://xplod-groups.com/locations/${city.toLowerCase()}`,
    isPartOf: {
      "@type": "WebSite",
      name: "XPLOD-GROUPS",
      url: "https://xplod-groups.com",
    },
    about: {
      "@type": "LocalBusiness",
      name: "XPLOD-GROUPS",
      description: `We deliver professional power tools to ${city} and surrounding areas`,
      areaServed: {
        "@type": "City",
        name: city,
        containedInPlace: {
          "@type": "State",
          name: "Kerala",
        },
      },
      makesOffer: {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: `Power Tools Delivery in ${city}`,
          description: serviceArea
            ? `Professional delivery service within ${serviceArea.distance} from our main branch`
            : "Professional delivery service available",
        },
      },
    },
    mainEntity: {
      "@type": "ItemList",
      name: `Popular Power Tools in ${city}`,
      itemListElement: products.slice(0, 8).map((product, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Product",
          name: product.name,
          url: `https://xplod-groups.com/products/${product.id}`,
          description: `${product.name} available for delivery in ${city}`,
        },
      })),
    },
  };
};

// Get all cities we serve for SEO targeting
export const getAllServiceCities = () => {
  return keralaServiceAreas.flatMap((area) => area.cities);
};

// Get service area info for a specific city
export const getServiceAreaForCity = (city) => {
  return keralaServiceAreas.find((area) => area.cities.includes(city));
};

// Generate location-specific meta descriptions
export const generateLocationMetaDescription = (city) => {
  const serviceArea = getServiceAreaForCity(city);
  const distance = serviceArea ? serviceArea.distance : "Available";

  return `Professional power tools available in ${city}, Kerala. Authorized STIHL dealer with home delivery (${distance} from Pathanamthitta). Expert service and competitive prices.`;
};
